import { MultiSelect, Select, Text } from '@mantine/core';
import type { EntityOption } from '../hooks/useEntityIndex';
import { useEntityIndex } from '../hooks/useEntityIndex';
import type { EntityKey } from '../lib/entities';
import { entityResource } from '../lib/entities';

/**
 * Picker data, under headings wherever the collection has them.
 *
 * A dozen storage areas in one flat list is unusable for the job the list exists for. Saying where
 * a bottle was thrown away means picking "the deep freezer" out of three of them, told apart only
 * by whatever prefix somebody typed into the name — and the first area added without the prefix
 * breaks even that. Under a "The Bar" heading it is one glance. A collection with no groups gets
 * exactly the flat list it always had.
 */
export function selectData(options: EntityOption[]) {
  const labelled = options.map((o) => ({
    value: o.value,
    label: o.hint ? `${o.label} — ${o.hint}` : o.label,
    group: o.group,
  }));
  if (!labelled.some((o) => o.group)) return labelled.map(({ value, label }) => ({ value, label }));

  // Insertion order, which is the order the endpoint returned — rooms come back in the order
  // somebody walks them. Sorting here would put The Bodega first because B beats K.
  const groups = new Map<string, { value: string; label: string }[]>();
  for (const { value, label, group } of labelled) {
    const heading = group ?? 'Not in a room yet';
    const bucket = groups.get(heading);
    if (bucket) bucket.push({ value, label });
    else groups.set(heading, [{ value, label }]);
  }
  return [...groups].map(([group, items]) => ({ group, items }));
}

/**
 * These are components rather than helper functions on purpose. Each one owns its
 * `useEntityIndex` call, which keeps the hook count stable no matter how many references a table
 * renders — the alternative, resolving every reference up in the parent, means calling hooks in a
 * loop over props and breaks the moment a page adds a column. TanStack Query dedupes the shared
 * cache entry, so a table of fifty rows still makes one request.
 */

/** Renders the name behind a stored id. */
export function EntityLabel({ entity, id }: { entity: EntityKey; id: unknown }) {
  const index = useEntityIndex(entity);
  const label = index.label(id);
  const unresolved = label === '—' || label === '…' || label === 'Not found';
  return (
    <Text size="sm" c={unresolved ? 'dimmed' : undefined} fs={label === 'Not found' ? 'italic' : undefined}>
      {label}
    </Text>
  );
}

export interface EntitySelectProps {
  entity: EntityKey;
  label?: string;
  description?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  required?: boolean;
  error?: React.ReactNode;
  disabled?: boolean;
}

/**
 * Choose a record by name. Replaces the free-text boxes that used to ask a manager to paste a
 * 26-character identifier — which is not a thing the portal displays anywhere, so those fields
 * were only fillable by someone with a database console open.
 */
export function EntitySelect({ entity, label, description, value, onChange, ...rest }: EntitySelectProps) {
  const index = useEntityIndex(entity);
  const resource = entityResource(entity);

  return (
    <Select
      label={label}
      description={description}
      placeholder={index.isLoading ? 'Loading…' : `Choose a ${resource.noun}`}
      data={selectData(index.options)}
      value={value ?? null}
      onChange={onChange}
      searchable={index.options.length > 8}
      nothingFoundMessage={`No ${resource.noun} matches`}
      // A value pointing at a deleted record would otherwise vanish silently and be saved away
      // on the next edit; keeping it visible makes the problem the user's to see and fix.
      allowDeselect={!rest.required}
      disabled={rest.disabled || index.isLoading}
      error={index.isError ? `Could not load the ${resource.noun} list` : rest.error}
      required={rest.required}
      comboboxProps={{ withinPortal: true }}
    />
  );
}

/**
 * Choose several records by name, for the columns that store a list of ids.
 *
 * Those columns used to be edited as raw JSON — a manager deciding which price lists a delivery
 * platform honours was asked to type an array of 26-character identifiers that appear nowhere on
 * screen. In practice that field could only ever be left at its default.
 */
export function EntityMultiSelect({
  entity,
  label,
  description,
  value,
  onChange,
  ...rest
}: Omit<EntitySelectProps, 'value' | 'onChange'> & {
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const index = useEntityIndex(entity);
  const resource = entityResource(entity);

  return (
    <MultiSelect
      label={label}
      description={description}
      placeholder={
        index.isLoading ? 'Loading…' : value.length > 0 ? undefined : `Choose any ${resource.noun}s`
      }
      data={selectData(index.options)}
      value={value}
      onChange={onChange}
      searchable={index.options.length > 8}
      nothingFoundMessage={`No ${resource.noun} matches`}
      clearable
      // Same reasoning as the single picker: an id left over from a deleted record stays visible
      // rather than disappearing and being silently dropped on the next save.
      hidePickedOptions
      disabled={rest.disabled || index.isLoading}
      error={index.isError ? `Could not load the ${resource.noun} list` : rest.error}
      required={rest.required}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
